/**
 * Konnect Automation Center — Sprint 1 UI
 * Reference: /app/memory/AUTOMATION_ENGINE_MASTER.md
 *
 * Sprint 1 delivers:
 *   - Overview / health summary
 *   - Active automations list (toggle, delete, run manually)
 *   - Templates library (one-click install)
 *   - Runs history (all-tenant)
 *
 * Later sprints add: visual builder, per-module panels, smart suggestions,
 * cross-module handlers, and the Onboarding Cascade wizard.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Zap, Play, Trash2, Sparkles, TrendingUp, Activity, Layers, Plus, Search, Pencil, Lock, Mic, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AutomationBuilderModal from '../components/automation/AutomationBuilderModal';
import SuggestedAutomationsPanel from '../components/automation/SuggestedAutomationsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/automation-engine`;

const authHeaders = () => {
  const token = localStorage.getItem('konnect_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AutomationCenterPage() {
  const [health, setHealth] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tplSearch, setTplSearch] = useState('');
  const [installingAll, setInstallingAll] = useState(false);
  const [currentTier, setCurrentTier] = useState('');           // v2.5.772
  const [currentTierName, setCurrentTierName] = useState('');   // v2.5.772
  // v2.5.857 — Voice/Text → Automation
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceText, setVoiceText] = useState('');
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const navigate = useNavigate();

  const installAllTemplates = async () => {
    if (installingAll) return;
    setInstallingAll(true);
    try {
      const r = await axios.post(`${API}/templates/install-all`, {}, { headers: authHeaders() });
      const lockedCount = (r.data.locked_by_tier || []).length;
      const msg = `Installed ${r.data.installed} automations`
        + (lockedCount ? ` · ${lockedCount} require an upgrade to unlock` : '');
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk install failed');
    } finally { setInstallingAll(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    // Promise.allSettled so ONE broken endpoint (e.g. /runs/all returning 500)
    // cannot swallow the response from a HEALTHY endpoint. Prior version used
    // Promise.all which caused Templates (0) even when /templates returned 32.
    const [h, a, t, r] = await Promise.allSettled([
      axios.get(`${API}/health/summary`, { headers: authHeaders() }),
      axios.get(API, { headers: authHeaders() }),
      axios.get(`${API}/templates`, { headers: authHeaders() }),
      axios.get(`${API}/runs/all?limit=25`, { headers: authHeaders() }),
    ]);
    if (h.status === 'fulfilled') setHealth(h.value.data);
    if (a.status === 'fulfilled') setAutomations(a.value.data.automations || []);
    if (t.status === 'fulfilled') {
      setTemplates(t.value.data.templates || []);
      setCurrentTier(t.value.data.current_tier || '');
      setCurrentTierName(t.value.data.current_tier_name || '');
    }
    if (r.status === 'fulfilled') setRuns(r.value.data.runs || []);
    // Surface failures so we don't fail silently in production either
    for (const [name, res] of [['health', h], ['automations', a], ['templates', t], ['runs', r]]) {
      if (res.status === 'rejected') {
        // eslint-disable-next-line no-console
        console.warn(`[AutomationCenter] ${name} endpoint failed:`, res.reason?.response?.status, res.reason?.message);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const installTemplate = async (tid) => {
    try {
      await axios.post(`${API}/templates/${tid}/install`, {}, { headers: authHeaders() });
      toast.success('Template installed. Toggle it on to activate.');
      load();
    } catch (e) {
      // 402 = tier lock — send to billing with upsell context
      if (e.response?.status === 402) {
        const d = e.response?.data?.detail || {};
        toast.error(d.message || 'This automation requires an upgrade.', {
          action: { label: 'Upgrade', onClick: () => navigate('/settings/billing') },
        });
        return;
      }
      toast.error('Install failed');
    }
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(`${API}/${a.id}`, { active: !a.active }, { headers: authHeaders() });
      load();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const removeAutomation = async (id) => {
    if (!window.confirm('Delete this automation?')) return;
    try {
      await axios.delete(`${API}/${id}`, { headers: authHeaders() });
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const runNow = async (id) => {
    try {
      const r = await axios.post(`${API}/${id}/execute`, { manual: true }, { headers: authHeaders() });
      toast.success(`Ran ${r.data.action_count} actions${r.data.success ? '' : ' (with errors)'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    }
  };

  // Group + filter templates for the Templates tab (Sprint 6 UX)
  const filteredTemplateCategories = useMemo(() => {
    const q = tplSearch.trim().toLowerCase();
    const filtered = q
      ? templates.filter(t =>
          (t.name || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q) ||
          (t.module || '').toLowerCase().includes(q) ||
          (t.tags || []).some(tag => tag.toLowerCase().includes(q))
        )
      : templates;
    const grouped = {};
    filtered.forEach(t => {
      const cat = t.category || 'Other';
      (grouped[cat] ||= []).push(t);
    });
    // Sort categories with Sales & Revenue first
    const order = ['Sales & Revenue', 'Customer Service', 'Customer Success', 'Human Resources', 'Finance & Accounting', 'Marketing', 'Operations', 'Analytics & Reporting'];
    return Object.entries(grouped).sort(([a], [b]) => {
      const ai = order.indexOf(a); const bi = order.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [templates, tplSearch]);

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="automation-center">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold" data-testid="automation-center-title">Automation Center</h1>
            <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] sm:text-xs">Sprints 1-6 · Live</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-medium">Intelligent Business Orchestration. Every module talks to every other module.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {currentTierName && (
            <div className="text-[11px] sm:text-xs text-gray-500 mr-1 sm:mr-2 w-full sm:w-auto" data-testid="current-tier-label">
              You&apos;re on <span className="font-semibold text-gray-700">{currentTierName}</span>
              {currentTier !== 'agency' && currentTier !== 'enterprise' && (
                <button onClick={() => navigate('/settings/billing')}
                  className="ml-1.5 text-amber-600 hover:text-amber-700 underline underline-offset-2"
                  data-testid="upgrade-tier-link">
                  Upgrade
                </button>
              )}
            </div>
          )}
          <Button onClick={installAllTemplates} disabled={installingAll} variant="default" size="sm" className="bg-amber-500 hover:bg-amber-600 h-9 text-xs sm:text-sm" data-testid="install-all-btn">
            <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" /> <span className="hidden sm:inline">{installingAll ? 'Installing…' : 'Install All Recommended'}</span><span className="sm:hidden">{installingAll ? '…' : 'Install All'}</span>
          </Button>
          <Button onClick={() => { setEditing(null); setBuilderOpen(true); }} size="sm" className="bg-emerald-brand hover:bg-emerald-hover h-9 text-xs sm:text-sm" data-testid="builder-open-btn">
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" /> <span className="hidden sm:inline">Build Automation</span><span className="sm:hidden">Build</span>
          </Button>
          <Button onClick={() => setVoiceOpen(true)} variant="outline" size="sm" className="border-violet-300 text-violet-700 hover:bg-violet-50 h-9 text-xs sm:text-sm" data-testid="voice-automation-btn">
            <Mic className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" /> <span className="hidden sm:inline">Voice / Text</span><span className="sm:hidden">Voice</span>
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="h-9 text-xs sm:text-sm" data-testid="refresh-btn">Refresh</Button>
        </div>
      </div>

      {/* Health summary cards */}
      {health && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <Card data-testid="stat-total">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-xs text-gray-medium truncate">Total Automations</div>
                  <div className="text-xl sm:text-2xl font-bold">{health.total}</div>
                </div>
                <Layers className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600/30 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-active">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-xs text-gray-medium truncate">Active</div>
                  <div className="text-xl sm:text-2xl font-bold text-emerald-600">{health.active}</div>
                </div>
                <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600/30 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-inactive">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-xs text-gray-medium truncate">Inactive</div>
                  <div className="text-xl sm:text-2xl font-bold text-gray-400">{health.inactive}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-runs-today">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-xs text-gray-medium truncate">Runs Today</div>
                  <div className="text-xl sm:text-2xl font-bold">{health.runs_today}</div>
                </div>
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600/30 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* v2.5.858 — Suggested Automations panel */}
      <SuggestedAutomationsPanel onAccept={load} />

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Run History</TabsTrigger>
        </TabsList>

        {/* ACTIVE */}
        <TabsContent value="active" className="space-y-3 mt-4">
          {loading && <div className="text-sm text-gray-medium">Loading…</div>}
          {!loading && automations.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-gray-medium">
                No automations yet. Install one from <b>Templates</b> to get started.
              </CardContent>
            </Card>
          )}
          {automations.map((a) => (
            <Card key={a.id} data-testid={`automation-${a.id}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="font-semibold text-sm sm:text-base break-words min-w-0 flex-1">{a.name}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{a.module}</Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{a.trigger?.type}</Badge>
                      {(a.tags || []).slice(0, 3).map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px] sm:text-xs bg-emerald-50 border-emerald-200 text-emerald-700">{t}</Badge>
                      ))}
                      {(a.tags || []).length > 3 && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs text-gray-500">+{(a.tags || []).length - 3}</Badge>
                      )}
                    </div>
                    {a.description && <div className="text-xs sm:text-sm text-gray-medium mt-2 line-clamp-2">{a.description}</div>}
                    <div className="text-[11px] sm:text-xs text-gray-400 mt-2">
                      Runs: {a.run_count || 0} · Success: {a.success_count || 0} · Failures: {a.failure_count || 0} · Actions: {(a.actions || []).length}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 justify-end border-t sm:border-t-0 pt-2 sm:pt-0">
                    <Switch checked={!!a.active} onCheckedChange={() => toggleActive(a)} data-testid={`toggle-${a.id}`} />
                    <Button size="sm" variant="outline" onClick={() => runNow(a.id)} disabled={!a.active} className="h-8 px-2 sm:px-3" data-testid={`run-${a.id}`}>
                      <Play className="h-3 w-3 sm:mr-1" /><span className="hidden sm:inline">Run</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditing(a); setBuilderOpen(true); }} className="h-8 w-8 p-0" data-testid={`edit-${a.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeAutomation(a.id)} className="h-8 w-8 p-0" data-testid={`delete-${a.id}`}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* TEMPLATES */}
        <TabsContent value="templates" className="space-y-3 mt-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={tplSearch} onChange={(e) => setTplSearch(e.target.value)}
              placeholder="Search 31 templates by name, module, or tag…"
              className="pl-9" data-testid="template-search" />
          </div>
          {filteredTemplateCategories.length === 0 && (
            <Card><CardContent className="p-6 text-center text-gray-medium text-sm">No templates match &quot;{tplSearch}&quot;.</CardContent></Card>
          )}
          {filteredTemplateCategories.map(([category, list]) => (
            <div key={category} className="space-y-2">
              <div className="flex items-center gap-2 pt-2">
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">{category}</div>
                <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
                <div className="flex-1 h-px bg-gray-border" />
              </div>
              {list.map((t) => (
                <Card key={t.id} data-testid={`template-${t.id}`}
                  className={t.available === false ? 'opacity-60 relative overflow-hidden' : ''}>
                  {t.available === false && (
                    <div className="absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-bl">
                      {t.upgrade_to?.toUpperCase() || 'UPGRADE'}
                    </div>
                  )}
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 flex-wrap">
                          {t.available === false
                            ? <Lock className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            : <Sparkles className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
                          <div className="font-semibold text-sm sm:text-base break-words min-w-0 flex-1">{t.name}</div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                          <Badge variant="outline" className="text-[10px] sm:text-xs">{t.module}</Badge>
                          {(t.tags || []).slice(0, 3).map(tag => (
                            <Badge key={tag} variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700">{tag}</Badge>
                          ))}
                        </div>
                        {t.description && <div className="text-xs sm:text-sm text-gray-medium mt-2 line-clamp-2">{t.description}</div>}
                        {t.available === false && (
                          <div className="text-[11px] sm:text-xs text-amber-600 mt-1 font-medium">
                            {t.locked_reason || 'Requires an upgrade to unlock'}
                          </div>
                        )}
                        <div className="text-[11px] sm:text-xs text-gray-400 mt-2">
                          Trigger: <code className="bg-gray-100 px-1 rounded">{t.trigger?.type}</code> · Actions: {t.actions?.length}
                        </div>
                      </div>
                      <div className="flex justify-end sm:block flex-shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0">
                        {t.available === false ? (
                          <Button size="sm" variant="outline" className="border-amber-400 text-amber-600 hover:bg-amber-50 h-9 text-xs sm:text-sm"
                            onClick={() => navigate('/settings/billing')}
                            data-testid={`upgrade-${t.id}`}>
                            <Lock className="h-3 w-3 mr-1" /> Upgrade
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => installTemplate(t.id)} className="h-9 text-xs sm:text-sm" data-testid={`install-${t.id}`}>Install</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ))}
        </TabsContent>

        {/* RUNS */}
        <TabsContent value="runs" className="space-y-2 mt-4">
          {runs.length === 0 && (
            <Card><CardContent className="p-6 text-center text-gray-medium text-sm">No runs yet. Install a template and click Run.</CardContent></Card>
          )}
          {runs.map((r) => (
            <Card key={r.id} data-testid={`run-${r.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.automation_name}</div>
                    <div className="text-xs text-gray-medium">
                      {new Date(r.started_at).toLocaleString()} · {r.action_count} actions · triggered by {r.triggered_by || 'system'}
                    </div>
                  </div>
                  <Badge className={r.success ? 'bg-emerald-100 text-emerald-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                    {r.success ? 'Success' : 'Error'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <div className="text-xs text-gray-400 text-center pt-6 border-t">
        Sprint 1 · Core Engine live. Sprints 2–6 delivered: onboarding cascade, revenue automations, cross-department crossovers, module depth (HR/Finance/Marketing/Analytics/Ops), and this visual builder.
        <br />See <code>/app/memory/AUTOMATION_ENGINE_MASTER.md</code> for the full roadmap.
      </div>

      <AutomationBuilderModal
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditing(null); }}
        onSaved={load}
        initial={editing}
      />

      {/* v2.5.857 — Voice / Text → Automation modal */}
      {voiceOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !voiceBusy && setVoiceOpen(false)} data-testid="voice-automation-modal">
          <div className="bg-white rounded-xl p-5 max-w-lg w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2"><Mic className="h-4 w-4 text-violet-600" /> Describe an automation in plain words</h3>
                <p className="text-xs text-gray-500 mt-1">Speak or type it — AI will draft the automation for you to review.</p>
              </div>
            </div>
            <textarea
              value={voiceText}
              onChange={e => setVoiceText(e.target.value)}
              rows={4}
              className="w-full border rounded-md px-3 py-2 text-sm mb-3"
              placeholder="e.g. Every Monday at 9am send a WhatsApp reminder to leads who haven't replied in 3 days"
              data-testid="voice-automation-text"
              disabled={voiceBusy || recording}
            />
            <div className="flex justify-between items-center gap-2">
              <Button
                variant="outline"
                onClick={async () => {
                  if (recording && mediaRecorder) {
                    mediaRecorder.stop();
                    return;
                  }
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    const rec = new MediaRecorder(stream);
                    const chunks = [];
                    rec.ondataavailable = e => chunks.push(e.data);
                    rec.onstop = async () => {
                      stream.getTracks().forEach(t => t.stop());
                      setRecording(false);
                      setMediaRecorder(null);
                      const blob = new Blob(chunks, { type: 'audio/webm' });
                      const fd = new FormData();
                      fd.append('file', blob, 'command.webm');
                      setVoiceBusy(true);
                      try {
                        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/voice-automation/command`, fd, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                        });
                        toast.success(`Draft automation created: "${data.automation.name}"`);
                        setVoiceText(data.transcript);
                        setTimeout(() => { setVoiceOpen(false); load(); }, 900);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Voice command failed');
                      } finally { setVoiceBusy(false); }
                    };
                    rec.start();
                    setMediaRecorder(rec);
                    setRecording(true);
                  } catch (err) {
                    toast.error('Microphone permission denied');
                  }
                }}
                className={recording ? 'border-red-400 text-red-600' : 'border-violet-300 text-violet-700'}
                disabled={voiceBusy}
                data-testid="voice-automation-record-btn"
              >
                {recording ? <><span className="animate-pulse mr-1">●</span> Stop & submit</> : <><Mic className="h-4 w-4 mr-1" /> Record</>}
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setVoiceOpen(false)} disabled={voiceBusy} data-testid="voice-automation-cancel">Cancel</Button>
                <Button
                  onClick={async () => {
                    if (!voiceText.trim()) return toast.error('Please describe the automation first');
                    setVoiceBusy(true);
                    try {
                      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/voice-automation/text-command`, { text: voiceText.trim() });
                      toast.success(`Draft automation created: "${data.automation.name}"`);
                      setTimeout(() => { setVoiceOpen(false); setVoiceText(''); load(); }, 700);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Could not create automation');
                    } finally { setVoiceBusy(false); }
                  }}
                  disabled={voiceBusy || recording}
                  className="bg-emerald-brand hover:bg-emerald-hover"
                  data-testid="voice-automation-submit"
                >
                  {voiceBusy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Working…</> : 'Create draft'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
